import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

const CloseIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
const Spinner = ({ className = "w-4 h-4" }) => <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;

const InviteDialog = ({ open, onClose, nodeId }) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('member'); // 'member' or 'admin'
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!email || loading) return;

        setLoading(true);
        setError(null);
        try {
            const sendInvite = httpsCallable(functions, 'sendInvite', { region: 'australia-southeast1' });
            await sendInvite({ nodeId, email, role });
            setSuccess(true);
            setTimeout(() => {
                onClose();
                setSuccess(false);
                setEmail('');
            }, 2000);
        } catch (err) {
            console.error("Invite Error:", err);
            setError(err.message || "Failed to send invitation.");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-[var(--bg-secondary)] border border-[var(--border-strong)] rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                <div className="p-6 border-b border-[var(--border-subtle)] flex items-center justify-between">
                    <h2 className="text-xl font-light tracking-tight text-[var(--text-main)]">Invite Collaborator</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] transition-colors">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleInvite} className="p-6 space-y-5">
                    {success ? (
                        <div className="py-8 text-center space-y-3">
                            <div className="w-12 h-12 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center mx-auto mb-4">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h3 className="text-lg font-medium text-[var(--text-main)]">Invitation Sent!</h3>
                            <p className="text-sm text-[var(--text-muted)]">Wait, we are closing the window...</p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest pl-1">Recipient Email</label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="dad@example.com"
                                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-sm text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]/50 transition-all"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest pl-1">Role</label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setRole('member')}
                                        className={`flex-1 py-2.5 rounded-xl border text-xs font-semibold transition-all ${role === 'member' ? 'bg-[var(--accent-primary)]/10 border-[var(--accent-primary)] text-[var(--accent-primary)]' : 'bg-[var(--bg-tertiary)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--text-muted)]'}`}
                                    >
                                        Member
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRole('admin')}
                                        className={`flex-1 py-2.5 rounded-xl border text-xs font-semibold transition-all ${role === 'admin' ? 'bg-[var(--accent-primary)]/10 border-[var(--accent-primary)] text-[var(--accent-primary)]' : 'bg-[var(--bg-tertiary)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--text-muted)]'}`}
                                    >
                                        Admin
                                    </button>
                                </div>
                                <p className="text-[10px] text-[var(--text-dim)] pl-1 mt-1 font-medium">
                                    {role === 'admin' ? 'Admins can manage settings and invite others.' : 'Members can view and collaborate in this space.'}
                                </p>
                            </div>

                            {error && (
                                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-medium">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 bg-[var(--text-main)] text-[var(--bg-main)] rounded-2xl text-sm font-bold hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
                            >
                                {loading ? <Spinner className="w-4 h-4 text-inherit" /> : 'Send Invitation'}
                            </button>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default InviteDialog;
